import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTeamUsers, useInviteUser } from '@/api/queries/auth.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { Badge } from '@/components/ui/badge';

const ROLES = ['OWNER','ADMIN','HR_MANAGER','HR','MANAGER','ACCOUNTANT'];
const ROLE_COLORS = { OWNER: 'purple', ADMIN: 'purple', HR_MANAGER: 'blue', HR: 'blue', MANAGER: 'green', ACCOUNTANT: 'warning' };

function InviteModal({ onClose }) {
  const { mutate, isPending } = useInviteUser();
  const [form, setForm] = useState({ phone: '', name: '', role: 'HR' });
  const inputCls = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  const handleInvite = () => {
    if (!form.phone.trim() || !form.name.trim()) { toast.error('Phone and name are required'); return; }
    mutate(form, {
      onSuccess: () => { toast.success(`Invite sent to ${form.phone}`); onClose(); },
      onError: () => toast.error('Failed to invite user'),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[18px] font-bold text-[var(--text-primary)]">Invite User</h2>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Phone Number</label>
            <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="9876543210" type="tel" className={inputCls}/>
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Full Name</label>
            <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Priya Sharma" className={inputCls}/>
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Role</label>
            <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} className={inputCls}>
              {ROLES.filter(r => r !== 'OWNER').map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
            </select>
          </div>
          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={handleInvite} disabled={isPending}>{isPending ? 'Inviting…' : 'Send Invite'}</Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const { data, isLoading } = useTeamUsers();
  const [showInvite, setShowInvite] = useState(false);

  const users = data?.data || [];

  return (
    <div className="space-y-5 fade-in max-w-2xl">
      <PageHeader title="Users & Access" breadcrumb={[{label:'Settings',href:'/settings'},{label:'Users'}]}
        actions={<Button size="sm" icon={Plus} onClick={() => setShowInvite(true)}>Invite User</Button>}/>
      <div className="card p-0 overflow-hidden">
        {isLoading && <div className="px-5 py-10 text-center text-[13px] text-[var(--text-tertiary)]">Loading…</div>}
        <div className="divide-y divide-[var(--border)]">
          {users.map(u => (
            <div key={u.id} className="flex items-center gap-4 px-5 py-4 hover:bg-[var(--bg-muted)]">
              <Avatar name={u.name} size="md"/>
              <div className="flex-1">
                <p className="text-[14px] font-semibold text-[var(--text-primary)]">{u.name}</p>
                <p className="text-[12px] text-[var(--text-secondary)]">{u.phone}{u.email ? ` · ${u.email}` : ''}</p>
              </div>
              <Badge variant={ROLE_COLORS[u.role] || 'default'} size="sm">{u.role.replace(/_/g,' ')}</Badge>
              <Badge variant="green" size="sm">ACTIVE</Badge>
            </div>
          ))}
          {!isLoading && users.length === 0 && (
            <div className="px-5 py-10 text-center text-[13px] text-[var(--text-tertiary)]">No users yet. Invite your team.</div>
          )}
        </div>
      </div>
      {showInvite && <InviteModal onClose={() => setShowInvite(false)}/>}
    </div>
  );
}
